import React, { useMemo, useState } from "react";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { ResolvedColumn, boardAvailableStatuses, boardColumns } from "./board-columns";
import "./board-workflows-page.scss";
import { BoardService } from "./board-service";
import ConfirmDialog from "./confirm-dialog";
import { Board, BoardColumn, BoardStatus } from "./types";
import { useBoardConfig } from "./use-board";

interface BoardColumnsEditorProps {
  board: Board;
  canManage: boolean;
}

// Map resolved columns to the persisted array (orderIndex normalized to position); the backend keeps
// existing column ids and mints ids only for new ones.
function toPayload(columns: ResolvedColumn[]): BoardColumn[] {
  return columns.map((column: ResolvedColumn, index: number) => ({
    id: column.id,
    name: column.name,
    statusIds: column.statusIds,
    orderIndex: index
  }));
}

// The board's columns: named lanes that each map to a set of the board's available statuses. A
// board-level view (statuses themselves are edited per project in the project hub's Workflow tab).
const BoardColumnsEditor: React.FC<BoardColumnsEditorProps> = ({ board, canManage }) => {
  const { config } = useBoardConfig();
  const [pendingColumnDelete, setPendingColumnDelete] = useState<ResolvedColumn | null>(null);

  const availableStatuses: BoardStatus[] = useMemo(
    () => boardAvailableStatuses(board, config.statuses),
    [board, config.statuses]
  );
  const columns: ResolvedColumn[] = useMemo(() => boardColumns(board, config.statuses), [board, config.statuses]);
  const unmappedStatuses: BoardStatus[] = useMemo(() => {
    const mapped: Set<string> = new Set<string>();
    for (const column of columns) for (const statusId of column.statusIds) mapped.add(statusId);
    return availableStatuses.filter((status: BoardStatus) => !mapped.has(status.id));
  }, [columns, availableStatuses]);

  const statusName = (statusId: string): string =>
    availableStatuses.find((status: BoardStatus) => status.id === statusId)?.name ?? statusId;
  const statusSelectOptions = availableStatuses.map((status: BoardStatus) => ({
    value: status.id,
    label: status.name
  }));

  const persist = (next: ResolvedColumn[]): void => {
    void BoardService.setBoardColumns(board.id, toPayload(next));
  };
  const renameColumn = (columnId: string, name: string): void => {
    persist(columns.map((column: ResolvedColumn) => (column.id === columnId ? { ...column, name } : column)));
  };
  const moveColumn = (index: number, direction: -1 | 1): void => {
    const target: number = index + direction;
    if (target < 0 || target >= columns.length) return;
    const next: ResolvedColumn[] = [...columns];
    [next[index], next[target]] = [next[target]!, next[index]!];
    persist(next);
  };
  const setColumnStatuses = (columnId: string, statusIds: string[]): void => {
    persist(columns.map((column: ResolvedColumn) => (column.id === columnId ? { ...column, statusIds } : column)));
  };
  const addColumn = (): void => {
    persist([...columns, { id: "", name: "New column", statusIds: [], statuses: [], orderIndex: columns.length }]);
  };
  const deleteColumn = (columnId: string): void => {
    persist(columns.filter((column: ResolvedColumn) => column.id !== columnId));
  };

  return (
    <div className="kuika-workflows">
      <section className="kuika-workflows__section">
        <div className="kuika-workflows__section-head">
          <div>
            <div className="kuika-workflows__section-title">Columns</div>
            <div className="kuika-workflows__section-sub">
              Columns are this board's lanes. Each maps to one or more of the project's statuses — e.g. an “In progress”
              column can cover In progress, In QA and In review. Dropping a card into a column moves it to that column's
              status.
            </div>
          </div>
          {canManage && (
            <KuikaButton type="secondary" size="sm" onClick={addColumn}>
              Add column
            </KuikaButton>
          )}
        </div>

        <div className="kuika-workflows__list">
          {columns.map((column: ResolvedColumn, index: number) => (
            <div key={column.id} className="kuika-workflows__column-row">
              {canManage && (
                <span className="kuika-workflows__reorder">
                  <button
                    type="button"
                    className="kuika-workflows__move"
                    disabled={index === 0}
                    onClick={() => moveColumn(index, -1)}
                    aria-label={`Move ${column.name} left`}
                    title="Move left"
                  >
                    <KuikaSvg name="chevron-up" type="arrows" width={11} height={11} />
                  </button>
                  <button
                    type="button"
                    className="kuika-workflows__move"
                    disabled={index === columns.length - 1}
                    onClick={() => moveColumn(index, 1)}
                    aria-label={`Move ${column.name} right`}
                    title="Move right"
                  >
                    <KuikaSvg name="chevron-down" type="arrows" width={11} height={11} />
                  </button>
                </span>
              )}
              {canManage ? (
                <KuikaInput
                  size="sm"
                  style={{ width: 160, flexShrink: 0 }}
                  value={column.name}
                  onChange={(_event, value: string | number) => renameColumn(column.id, String(value))}
                />
              ) : (
                <span className="kuika-workflows__readonly-name" style={{ flex: "0 0 160px" }}>
                  {column.name}
                </span>
              )}
              {canManage ? (
                <span className="kuika-workflows__column-statuses">
                  <KuikaSelectbox
                    size="sm"
                    mode="multiple"
                    dropdownMatchSelectWidth={false}
                    placeholder="Statuses in this column…"
                    value={column.statusIds}
                    onChange={(value: string | number | (string | number)[] | null) =>
                      setColumnStatuses(column.id, Array.isArray(value) ? value.map(String) : [])
                    }
                    options={statusSelectOptions}
                  />
                </span>
              ) : (
                <span className="kuika-workflows__category-label">
                  {column.statusIds.map(statusName).join(", ") || "No statuses"}
                </span>
              )}
              {canManage && (
                <button
                  type="button"
                  className="kuika-workflows__remove"
                  disabled={columns.length <= 1}
                  onClick={() => setPendingColumnDelete(column)}
                  aria-label={`Delete column ${column.name}`}
                  title="Delete column"
                >
                  <KuikaSvg name="trash" width={14} height={14} />
                </button>
              )}
            </div>
          ))}
        </div>

        {unmappedStatuses.length > 0 && (
          <p className="kuika-workflows__hint kuika-workflows__hint--warn">
            Not on any column: {unmappedStatuses.map((status: BoardStatus) => status.name).join(", ")}. Issues in these
            statuses won't show on the board until a column includes them.
          </p>
        )}
      </section>

      <ConfirmDialog
        open={pendingColumnDelete !== null}
        title="Delete column?"
        message={`"${pendingColumnDelete?.name ?? ""}" will be removed from this board. Its statuses (${(pendingColumnDelete?.statusIds ?? []).map(statusName).join(", ") || "none"}) stay — reassign them to another column or they'll be hidden.`}
        confirmLabel="Delete"
        cancelLabel="Keep"
        danger
        onConfirm={() => {
          if (pendingColumnDelete) deleteColumn(pendingColumnDelete.id);
          setPendingColumnDelete(null);
        }}
        onCancel={() => setPendingColumnDelete(null)}
      />
    </div>
  );
};

export default BoardColumnsEditor;
